import React, { useState, useEffect, useRef } from 'react';
import { Upload, FileText, Clock, CheckCircle2, AlertCircle, Eye, X as CloseIcon, Maximize2, Minimize2, BarChart3, Calendar, Timer } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { storage, db } from '../../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, setDoc, updateDoc, addDoc, collection, query, orderBy, where, limit, getDocs, onSnapshot } from 'firebase/firestore';
import UploadSection from './components/UploadSection';
import PDFPreviewModal from './components/PDFPreviewModal';
import SessionDetailsModal from './components/SessionDetailsModal';
import AnalyticsSessions from './components/AnalyticsSessions';
import TipsSection from './components/TipsSection';

const PitchDeck = ({ formData, onInputChange, companyId }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [analyticsHistory, setAnalyticsHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  
  // Generate unique session ID for this browser session
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Analytics tracking refs
  const sessionStartTime = useRef(Date.now());
  const pdfViewStartTime = useRef(null);
  const currentSlideStartTime = useRef(null);
  const currentSlideNumber = useRef(null);
  const slideViewSequence = useRef(0);
  const iframeRef = useRef(null);
  
  const [analytics, setAnalytics] = useState({
    sessionId: sessionId,
    totalTimeOnScreen: 0,
    pdfViewTime: 0,
    uploadAttempts: 0,
    previewCount: 0,
    slidesViewed: [], // Array of slide numbers viewed
    slideViewTimes: {}, // { slideNumber: totalTimeMs }
    slideViewDetails: {}, // { slideNumber: [{ startTime, endTime, duration, sequence }] }
    totalSlideViews: 0,
    uniqueSlidesViewed: 0,
    averageTimePerSlide: 0,
    mostViewedSlide: null,
    leastViewedSlide: null,
    historicalData: null
  });

  // Real-time analytics listener
  useEffect(() => {
    if (!companyId) return;

    const analyticsQuery = query(
      collection(db, 'analytics'),
      where('companyId', '==', companyId),
      orderBy('timestamp', 'desc'),
      limit(500)
    );

    const unsubscribe = onSnapshot(analyticsQuery, (querySnapshot) => {
      const historyData = [];
      querySnapshot.forEach((doc) => {
        historyData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`Real-time update: ${historyData.length} analytics events loaded`);
      setAnalyticsHistory(historyData);
      
      if (historyData.length > 0) {
        calculateHistoricalStats(historyData);
      }
    }, (error) => {
      console.error('Real-time analytics listener error:', error);
      // Fallback to manual loading
      loadAnalyticsHistory();
    });

    return () => unsubscribe();
  }, [companyId]);

  // Load analytics history manually (fallback)
  const loadAnalyticsHistory = async () => {
    if (!companyId) return;
    
    setLoadingHistory(true);
    try {
      const analyticsQuery = query(
        collection(db, 'analytics'),
        where('companyId', '==', companyId),
        orderBy('timestamp', 'desc'),
        limit(500)
      );
      
      const querySnapshot = await getDocs(analyticsQuery);
      const historyData = [];
      
      querySnapshot.forEach((doc) => {
        historyData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`Manual load: ${historyData.length} analytics events loaded`);
      setAnalyticsHistory(historyData);
      
      if (historyData.length > 0) {
        calculateHistoricalStats(historyData);
      }
      
    } catch (error) {
      console.error('Error loading analytics history:', error);
      setAnalyticsHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const calculateHistoricalStats = (historyData) => {
    const slideTimeData = {};
    const slideViewCounts = {};
    let totalViewTime = 0;
    let totalSessions = new Set();
    
    historyData.forEach(event => {
      const slideNumber = event.slideNumber || event.data?.slideNumber;
      const timeSpent = event.timeSpentMs || event.data?.timeSpentMs || 0;
      const sessionId = event.sessionId;
      
      if (sessionId) {
        totalSessions.add(sessionId);
      }
      
      if (slideNumber && timeSpent > 0) {
        if (!slideTimeData[slideNumber]) {
          slideTimeData[slideNumber] = [];
          slideViewCounts[slideNumber] = 0;
        }
        slideTimeData[slideNumber].push(timeSpent);
        slideViewCounts[slideNumber]++;
        totalViewTime += timeSpent;
      }
    });
    
    // Calculate statistics
    let mostViewed = { slide: null, count: 0 };
    let leastViewed = { slide: null, count: Infinity };
    let totalSlideTime = 0;
    let totalSlideEntries = 0;
    
    Object.entries(slideViewCounts).forEach(([slide, count]) => {
      if (count > mostViewed.count) {
        mostViewed = { slide: parseInt(slide), count };
      }
      if (count < leastViewed.count && count > 0) {
        leastViewed = { slide: parseInt(slide), count };
      }
      
      if (slideTimeData[slide]) {
        const avgTimeForSlide = slideTimeData[slide].reduce((a, b) => a + b, 0) / slideTimeData[slide].length;
        totalSlideTime += avgTimeForSlide;
        totalSlideEntries++;
      }
    });
    
    const historicalData = {
      totalSlideViewsAllTime: Object.values(slideViewCounts).reduce((a, b) => a + b, 0),
      averageTimePerSlideAllTime: totalSlideEntries > 0 ? Math.round(totalSlideTime / totalSlideEntries) : 0,
      mostViewedSlideAllTime: mostViewed.slide,
      leastViewedSlideAllTime: leastViewed.count !== Infinity ? leastViewed.slide : null,
      slideTimeHistory: slideTimeData,
      slideViewHistory: slideViewCounts,
      totalSessions: totalSessions.size,
      totalViewTimeAllTime: totalViewTime
    };
    
    setAnalytics(prev => ({
      ...prev,
      historicalData
    }));
  };

  // Enhanced slide change tracking
  const handleSlideChange = async (slideNumber) => {
    const currentTime = Date.now();
    
    // End tracking for previous slide
    if (currentSlideStartTime.current && currentSlideNumber.current && currentSlideNumber.current !== slideNumber) {
      const timeOnSlide = currentTime - currentSlideStartTime.current;
      const prevSlide = currentSlideNumber.current;
      
      // Update analytics state
      setAnalytics(prev => {
        const newSlideViewTimes = {
          ...prev.slideViewTimes,
          [prevSlide]: (prev.slideViewTimes[prevSlide] || 0) + timeOnSlide
        };
        
        const newSlideViewDetails = {
          ...prev.slideViewDetails,
          [prevSlide]: [
            ...(prev.slideViewDetails[prevSlide] || []),
            {
              startTime: currentSlideStartTime.current,
              endTime: currentTime,
              duration: timeOnSlide,
              sequence: slideViewSequence.current,
              timestamp: new Date().toISOString()
            }
          ]
        };
        
        return {
          ...prev,
          slideViewTimes: newSlideViewTimes,
          slideViewDetails: newSlideViewDetails
        };
      });

      // Track slide view completion
      await trackAnalytics('slide_view_complete', {
        slideNumber: prevSlide,
        timeSpentMs: timeOnSlide,
        timeSpentSeconds: Math.round(timeOnSlide / 1000 * 100) / 100,
        viewSequence: slideViewSequence.current,
        sessionDuration: currentTime - sessionStartTime.current,
        isRevisit: analytics.slidesViewed.includes(prevSlide),
        viewerInfo: formData.viewerInfo
      });
    }

    // Start tracking new slide
    currentSlideStartTime.current = currentTime;
    currentSlideNumber.current = slideNumber;
    slideViewSequence.current++;
    
    setAnalytics(prev => {
      const newSlidesViewed = prev.slidesViewed.includes(slideNumber) 
        ? prev.slidesViewed 
        : [...prev.slidesViewed, slideNumber];
      
      return {
        ...prev,
        currentSlide: slideNumber,
        slidesViewed: newSlidesViewed,
        totalSlideViews: prev.totalSlideViews + 1,
        uniqueSlidesViewed: newSlidesViewed.length
      };
    });

    // Track slide view start
    await trackAnalytics('slide_view_start', {
      slideNumber: slideNumber,
      viewSequence: slideViewSequence.current,
      isFirstTimeViewing: !analytics.slidesViewed.includes(slideNumber),
      sessionDuration: currentTime - sessionStartTime.current,
      totalSlidesInDeck: formData.pitchDeck.slides || 0,
      viewerInfo: formData.viewerInfo
    });
  };

  // Enhanced analytics tracking
  const trackAnalytics = async (eventType, data = {}) => {
    try {
      const analyticsData = {
        // Core identification
        companyId,
        sessionId,
        eventType,
        timestamp: new Date().toISOString(),
        timestampMs: Date.now(),
        
        // Session context
        sessionDurationMs: Date.now() - sessionStartTime.current,
        sessionStartTime: sessionStartTime.current,
        
        // Environment context
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        
        // Viewer information
        viewerInfo: formData.viewerInfo || null
      };

      // Add event-specific data
      if (eventType === 'slide_view_complete') {
        analyticsData.slideNumber = data.slideNumber;
        analyticsData.timeSpentMs = data.timeSpentMs;
        analyticsData.timeSpentSeconds = data.timeSpentSeconds;
        analyticsData.viewSequence = data.viewSequence;
        analyticsData.isRevisit = data.isRevisit;
      } else if (eventType === 'slide_view_start') {
        analyticsData.slideNumber = data.slideNumber;
        analyticsData.viewSequence = data.viewSequence;
        analyticsData.isFirstTimeViewing = data.isFirstTimeViewing;
        analyticsData.totalSlidesInDeck = data.totalSlidesInDeck;
      } else if (eventType === 'pdf_preview_session_complete') {
        analyticsData.totalViewTimeMs = data.totalViewTimeMs;
        analyticsData.slidesViewed = data.slidesViewed;
        analyticsData.uniqueSlidesViewed = data.uniqueSlidesViewed;
        analyticsData.totalSlideViews = data.totalSlideViews;
        analyticsData.completionRate = data.completionRate;
      }

      // Store in analytics collection
      await addDoc(collection(db, 'analytics'), analyticsData);
      
      // Update company summary
      const summaryRef = doc(db, 'companies', companyId);
      await updateDoc(summaryRef, {
        'pitchDeck.lastAnalyticsUpdate': new Date().toISOString(),
        'pitchDeck.totalAnalyticsEvents': analytics.totalSlideViews + 1,
        'pitchDeck.lastSessionId': sessionId
      });
      
      console.log(`Analytics tracked: ${eventType}`, analyticsData);
      
    } catch (error) {
      console.error('Error tracking analytics:', error);
    }
  };

  // Group analytics by session with enhanced data
  const getRecentSessions = () => {
    const sessionGroups = {};
    
    analyticsHistory.forEach(event => {
      const sessionId = event.sessionId;
      if (!sessionId) return;
      
      if (!sessionGroups[sessionId]) {
        sessionGroups[sessionId] = {
          sessionId,
          events: [],
          startTime: event.sessionStartTime,
          endTime: event.timestampMs,
          totalDuration: event.sessionDurationMs || 0,
          viewerInfo: event.viewerInfo,
          deviceInfo: event.userAgent,
          viewport: event.viewport,
          eventTypes: new Set()
        };
      }
      
      const session = sessionGroups[sessionId];
      session.events.push(event);
      session.eventTypes.add(event.eventType);
      
      // Update session timing
      if (event.timestampMs < session.startTime) session.startTime = event.timestampMs;
      if (event.timestampMs > session.endTime) session.endTime = event.timestampMs;
      
      // Track slide-specific data
      if (event.eventType === 'slide_view_complete') {
        if (!session.slideViewTimes) session.slideViewTimes = {};
        if (!session.slidesViewed) session.slidesViewed = new Set();
        
        session.slidesViewed.add(event.slideNumber);
        session.slideViewTimes[event.slideNumber] = (session.slideViewTimes[event.slideNumber] || 0) + (event.timeSpentMs || 0);
      }
    });
    
    // Convert to array and calculate additional metrics
    return Object.values(sessionGroups)
      .map(session => {
        const startTime = new Date(session.startTime);
        const endTime = new Date(session.endTime);
        const sessionDuration = endTime - startTime;
        
        // Create chart data for slides
        const slideChartData = session.slideViewTimes ? 
          Object.entries(session.slideViewTimes)
            .map(([slide, time]) => ({
              slide: `Slide ${slide}`,
              slideNumber: parseInt(slide),
              timeSeconds: Math.round(time / 1000 * 100) / 100,
              timeMinutes: Math.round(time / 60000 * 100) / 100
            }))
            .sort((a, b) => a.slideNumber - b.slideNumber) : [];
        
        return {
          ...session,
          slidesViewed: session.slidesViewed ? Array.from(session.slidesViewed).sort((a, b) => a - b) : [],
          eventTypes: Array.from(session.eventTypes),
          duration: sessionDuration,
          slideChartData,
          completionRate: session.slidesViewed ? 
            Math.round((session.slidesViewed.size / (formData.pitchDeck.slides || 1)) * 100) : 0,
          avgTimePerSlide: session.slidesViewed && session.slidesViewed.size > 0 ? 
            Math.round(session.totalDuration / session.slidesViewed.size / 1000) : 0
        };
      })
      .sort((a, b) => new Date(b.startTime) - new Date(a.startTime))
      .slice(0, 20);
  };

  // Simulate slide changes for PDF interaction
  useEffect(() => {
    let intervalId;
    
    if (previewOpen && formData.pitchDeck.url) {
      // Simulate slide changes every 8-15 seconds during preview
      intervalId = setInterval(() => {
        const randomSlide = Math.floor(Math.random() * (formData.pitchDeck.slides || 10)) + 1;
        // Only change if it's different from current or if it matches realistic user behavior
        if (randomSlide !== currentSlideNumber.current || Math.random() > 0.7) {
          handleSlideChange(randomSlide);
        }
      }, Math.random() * 7000 + 8000); // 8-15 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [previewOpen]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAnalytics(prev => ({ ...prev, uploadAttempts: prev.uploadAttempts + 1 }));
    await trackAnalytics('upload_attempt', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    if (!file.type.includes('pdf') && !file.type.includes('powerpoint')) {
      setError('Please upload a PDF or PowerPoint file');
      await trackAnalytics('upload_error', { error: 'invalid_file_type', fileType: file.type });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      await trackAnalytics('upload_error', { error: 'file_too_large', fileSize: file.size });
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploadStartTime = Date.now();
      
      const storageRef = ref(storage, `users/demoUser/pitchDeck/${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const uploadDuration = Date.now() - uploadStartTime;

      const pitchDeckDoc = doc(db, 'companies', companyId);
      await setDoc(pitchDeckDoc, {
        pitchDeck: {
          fileName: file.name,
          url: downloadURL,
          uploaded: true,
          lastUpdated: new Date().toISOString(),
          slides: Math.floor(Math.random() * 15) + 10, // Simulated slide count
        }
      }, { merge: true });

      onInputChange('pitchDeck', 'uploaded', true);
      onInputChange('pitchDeck', 'fileName', file.name);
      onInputChange('pitchDeck', 'slides', Math.floor(Math.random() * 15) + 10);
      onInputChange('pitchDeck', 'lastUpdated', new Date().toISOString());
      onInputChange('pitchDeck', 'file', file);
      onInputChange('pitchDeck', 'url', downloadURL);

      await trackAnalytics('upload_success', {
        fileName: file.name,
        fileSize: file.size,
        uploadDurationMs: uploadDuration
      });

    } catch (error) {
      setError('Failed to upload file. Please try again.');
      await trackAnalytics('upload_error', { error: 'upload_failed', errorMessage: error.message });
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async () => {
    setPreviewOpen(true);
    setIsFullscreen(true);
    pdfViewStartTime.current = Date.now();
    
    setAnalytics(prev => ({ ...prev, previewCount: prev.previewCount + 1 }));
    await trackAnalytics('pdf_preview_opened', {
      slideCount: formData.pitchDeck.slides,
      previewNumber: analytics.previewCount + 1,
      viewerInfo: formData.viewerInfo
    });

    // Start with slide 1
    setTimeout(() => {
      handleSlideChange(1);
    }, 1000);
  };

  const handleClosePreview = async () => {
    // Track final slide time
    if (currentSlideStartTime.current && currentSlideNumber.current) {
      const finalSlideTime = Date.now() - currentSlideStartTime.current;
      
      await trackAnalytics('slide_view_complete', {
        slideNumber: currentSlideNumber.current,
        timeSpentMs: finalSlideTime,
        viewSequence: slideViewSequence.current,
        isFinalSlide: true,
        sessionDuration: Date.now() - sessionStartTime.current,
        viewerInfo: formData.viewerInfo
      });
    }

    // Track session completion
    if (pdfViewStartTime.current) {
      const totalViewTime = Date.now() - pdfViewStartTime.current;
      
      await trackAnalytics('pdf_preview_session_complete', {
        totalViewTimeMs: totalViewTime,
        slidesViewed: analytics.slidesViewed,
        uniqueSlidesViewed: analytics.uniqueSlidesViewed,
        totalSlideViews: analytics.totalSlideViews,
        slideViewTimes: analytics.slideViewTimes,
        completionRate: Math.round((analytics.uniqueSlidesViewed / (formData.pitchDeck.slides || 1)) * 100),
        sessionId: sessionId,
        viewerInfo: formData.viewerInfo
      });
    }
    
    // Reset tracking
    currentSlideStartTime.current = null;
    currentSlideNumber.current = null;
    pdfViewStartTime.current = null;
    
    setPreviewOpen(false);
    setIsFullscreen(false);
  };

  const openSessionModal = (session) => {
    setSelectedSession(session);
    setSessionModalOpen(true);
  };

  // Create sample analytics data for testing
  const createSampleAnalytics = async () => {
    if (process.env.NODE_ENV !== 'development') return;
    
    try {
      const sampleSessionId = `sample_${Date.now()}`;
      const slides = [1, 2, 3, 1, 4, 5, 2, 6, 7, 8, 3, 9, 10];
      const slideTimes = [12000, 25000, 18000, 8000, 22000, 15000, 12000, 28000, 20000, 16000, 14000, 30000, 25000];
      
      for (let i = 0; i < slides.length; i++) {
        await addDoc(collection(db, 'analytics'), {
          companyId,
          sessionId: sampleSessionId,
          eventType: i === 0 ? 'slide_view_start' : 'slide_view_complete',
          timestamp: new Date(Date.now() - (slides.length - i) * 30000).toISOString(),
          timestampMs: Date.now() - (slides.length - i) * 30000,
          slideNumber: slides[i],
          timeSpentMs: i > 0 ? slideTimes[i-1] : 0,
          viewSequence: i + 1,
          fileName: formData.pitchDeck.fileName || 'sample-deck.pdf',
          totalSlides: formData.pitchDeck.slides || 15
        });
      }
      
      // Add session complete event
      await addDoc(collection(db, 'analytics'), {
        companyId,
        sessionId: sampleSessionId,
        eventType: 'pdf_preview_session_complete',
        timestamp: new Date().toISOString(),
        timestampMs: Date.now(),
        totalViewTimeMs: slideTimes.reduce((a, b) => a + b, 0),
        slidesViewed: [...new Set(slides)],
        uniqueSlidesViewed: new Set(slides).size,
        totalSlideViews: slides.length,
        fileName: formData.pitchDeck.fileName || 'sample-deck.pdf'
      });
      
      console.log('Sample analytics data created');
      
    } catch (error) {
      console.error('Error creating sample analytics:', error);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-4 lg:px-8 max-w-7xl mx-auto">
      <UploadSection
        formData={formData}
        onInputChange={onInputChange}
        uploading={uploading}
        error={error}
        handleFileUpload={handleFileUpload}
        setError={setError}
        setUploading={setUploading}
        handlePreview={handlePreview}
        companyId={companyId}
      />

      <PDFPreviewModal
        previewOpen={previewOpen}
        formData={formData}
        isFullscreen={isFullscreen}
        setIsFullscreen={setIsFullscreen}
        handleClosePreview={handleClosePreview}
        currentSlideNumber={currentSlideNumber}
        trackAnalytics={trackAnalytics}
        analytics={analytics}
        iframeRef={iframeRef}
      />

      <SessionDetailsModal
        sessionModalOpen={sessionModalOpen}
        selectedSession={selectedSession}
        setSessionModalOpen={setSessionModalOpen}
      />

      <AnalyticsSessions
        analyticsHistory={analyticsHistory}
        loadingHistory={loadingHistory}
        getRecentSessions={getRecentSessions}
        loadAnalyticsHistory={loadAnalyticsHistory}
        createSampleAnalytics={createSampleAnalytics}
        openSessionModal={openSessionModal}
        processEnv={process.env.NODE_ENV}
      />

      <TipsSection />
    </div>
  );
};

export default PitchDeck;